import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { all_routes } from "../../../Router/all_routes";
import { ArrowLeft, ChevronDown, ChevronUp, Info } from "feather-icons-react/build/IconComponents";
import { useDispatch, useSelector } from "react-redux";
import { setToogleHeader } from "../../../core/redux/action";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import Swal from "sweetalert2";
import Select from "react-select";
import { api_post, api_get } from "../../../helpers/index";

const EditTier = () => {
  const route = all_routes;
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const navigate = useNavigate();
  const location = useLocation();

  // Get tier_id from query string
  const searchParams = new URLSearchParams(location.search);
  const tier_id = searchParams.get("tier_id");

  // State for tier details
  const [tierName, setTierName] = useState("");
  const [description, setDescription] = useState("");
  const [probabilityWithRank, setProbabilityWithRank] = useState("");
  const [prize, setPrize] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [errors, setErrors] = useState({});
  const [totalRanks, setTotalRanks] = useState(0);
  const [tierSequence, setTierSequence] = useState("");

  // Fetch available prizes for the dropdown
  const fetchPrizes = async () => {
    try {
      const result = await api_get("spinwheel/prize/list/1");
      if (result.status) {
        const totalPages = result.data.pagination.total_page;
        let allPrizes = result.data.collection;

        for (let page = 2; page <= totalPages; page++) {
          const pageResult = await api_get(`spinwheel/prize/list/${page}`);
          if (pageResult.status) {
            allPrizes = allPrizes.concat(pageResult.data.collection);
          }
        }

        const prizeOptions = allPrizes.map((item) => ({
          value: item.prize_id,
          label: item.name,
        }));
        setPrizes(prizeOptions);
        return prizeOptions; // Return prizeOptions for immediate use
      }
      return [];
    } catch (error) {
      console.error("Error fetching prizes:", error);
      return [];
    }
  };

  // Add function to fetch total ranks
  const fetchTotalRanks = async () => {
    try {
      const result = await api_get("rank/length");
      if (result.status === 1) {
        setTotalRanks(result.data);
      }
    } catch (error) {
      console.error("Error fetching total ranks:", error);
    }
  };

  // Fetch tier details
  const fetchTierDetails = async (prizeOptions) => {
    try {
      const response = await api_get(`spinwheel/tier/get/${tier_id}`);
      if (response.status === 1) {
        const { description, probability_with_rank, tier_sequence, prize_info } = response.data;
        setTierName(response.data.name);
        setDescription(description);
        setProbabilityWithRank(probability_with_rank);
        setTierSequence(tier_sequence);
        
        // Set prize using the prizeOptions passed from fetchPrizes
        const matchingPrize = prizeOptions.find(p => p.value === prize_info.prize_id);
        if (matchingPrize) {
          setPrize(matchingPrize);
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load tier details.",
        });
      }
    } catch (error) {
      console.error("Error fetching tier details:", error);
    }
  };

  // Single useEffect to handle all data loading
  useEffect(() => {
    const initializeData = async () => {
      // Fetch prizes first and get the prizeOptions
      const prizeOptions = await fetchPrizes();
      
      // If we have a tier_id, fetch tier details using the prizeOptions
      if (tier_id) {
        await fetchTierDetails(prizeOptions);
      }
      
      // Fetch total ranks
      await fetchTotalRanks();
    };

    initializeData();
  }, [tier_id]); // Only depend on tier_id changes

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  // Validate input fields
  const validateInputs = () => {
    const newErrors = {};
    if (!tierName.trim()) newErrors.tierName = "Tier name is required.";
    if (!description.trim()) newErrors.description = "Description is required.";
    if (description.trim().length > 60) newErrors.description = "Description must not exceed 60 characters.";
    if (!probabilityWithRank.trim()) {
      newErrors.probabilityWithRank = "Probability with Rank % is required.";
    } else {
      const values = probabilityWithRank.split(",").map(v => v.trim());
      
      if (values.length !== totalRanks) {
        newErrors.probabilityWithRank = `Must provide ${totalRanks} comma-separated values (one for each rank)`;
      } else {
        for (let i = 0; i < values.length; i++) {
          const val = parseFloat(values[i]);
          if (isNaN(val) || val < 0 || val > 100) {
            newErrors.probabilityWithRank = `All values must be numbers between 0 and 100. Invalid value at position ${i + 1}`;
            break;
          }
        }
      }
    }
    if (!prize) newErrors.prize = "Prize selection is required.";
    if (!tierSequence) newErrors.tierSequence = "Tier sequence is required.";
    if (isNaN(tierSequence) || tierSequence < 0) {
      newErrors.tierSequence = "Tier sequence must be a non-negative number.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle saving the updated tier
  const handleSaveTier = async () => {
    if (!validateInputs()) {
      return;
    }

    const body = {
      name: tierName,
      description: description,
      prize_id: prize.value,
      probability_with_rank: probabilityWithRank,
      tier_sequence: parseInt(tierSequence),
    };
    try {
      const result = await api_post(`spinwheel/tier/update/${tier_id}`, body);
      if (result.status === 1) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Tier updated successfully!",
        }).then(() => {
          navigate(route.tierList);
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update tier. Please try again later.",
        });
      }
    } catch (error) {
      console.error("Error updating tier:", error);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Edit Tier</h4>
              <h6>Update tier details</h6>
            </div>
          </div>
          <ul className="table-top-head">
            <li>
              <div className="page-btn">
                <Link to={route.tierList} className="btn btn-secondary">
                  <ArrowLeft className="me-2" />
                  Back to Tier List
                </Link>
              </div>
            </li>
            <li>
              <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                <Link
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="Collapse"
                  id="collapse-header"
                  className={data ? "active" : ""}
                  onClick={() => {
                    dispatch(setToogleHeader(!data));
                  }}
                >
                  <ChevronUp className="feather-chevron-up" />
                </Link>
              </OverlayTrigger>
            </li>
          </ul>
        </div>
        <form>
          <div className="card">
            <div className="card-body add-prize pb-0">
              <div className="accordion-card-one accordion" id="accordionExample">
                <div className="accordion-item">
                  <div className="accordion-header" id="headingOne">
                    <div
                      className="accordion-button"
                      data-bs-toggle="collapse"
                      data-bs-target="#collapseOne"
                      aria-controls="collapseOne"
                    >
                      <div className="addprize-icon">
                        <h5>
                          <Info className="add-info" />
                          <span>Tier Information</span>
                        </h5>
                        <Link to="#">
                          <ChevronDown className="chevron-down-add" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div
                    id="collapseOne"
                    className="accordion-collapse collapse show"
                    aria-labelledby="headingOne"
                    data-bs-parent="#accordionExample"
                  >
                    <div className="accordion-body">
                      <div className="row">
                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3 add-prize">
                            <label className="form-label">Tier Name</label>
                            <input
                              type="text"
                              className="form-control"
                              value={tierName}
                              onChange={(e) => setTierName(e.target.value)}
                            />
                            {errors.tierName && (
                              <div className="text-danger mt-1">{errors.tierName}</div>
                            )}
                          </div>
                        </div>

                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3 add-prize">
                            <label className="form-label">Prize</label>
                            <Select
                              classNamePrefix="react-select"
                              options={prizes}
                              placeholder="Choose"
                              value={prize}
                              onChange={(selectedOption) => setPrize(selectedOption)}
                            />
                            {errors.prize && (
                              <div className="text-danger mt-1">{errors.prize}</div>
                            )}
                          </div>
                        </div>

                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3 add-prize">
                            <label className="form-label">
                              Probability With Rank % ({totalRanks} ranks)
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={probabilityWithRank}
                              onChange={(e) => setProbabilityWithRank(e.target.value)}
                              placeholder={`Enter ${totalRanks} values (e.g., 0,0,0,0,0,0,10)`}
                            />
                            {errors.probabilityWithRank && (
                              <div className="text-danger mt-1">{errors.probabilityWithRank}</div>
                            )}
                            <small className="form-text text-muted">
                              Enter {totalRanks} comma-separated values (0-100), one for each rank from left to right
                            </small>
                          </div>
                        </div>

                        <div className="col-lg-4 col-sm-6 col-12">
                          <div className="mb-3 add-prize">
                            <label className="form-label">Tier Sequence</label>
                            <input
                              type="number"
                              className="form-control"
                              value={tierSequence}
                              onChange={(e) => setTierSequence(e.target.value)}
                            />
                            {errors.tierSequence && (
                              <div className="text-danger mt-1">{errors.tierSequence}</div>
                            )}
                          </div>
                        </div>

                        <div className="col-lg-12">
                          <div className="input-blocks summer-description-box transfer mb-3">
                            <label>Description</label>
                            <textarea
                              className="form-control h-100"
                              rows={5}
                              value={description}
                              onChange={(e) => setDescription(e.target.value)}
                            />
                            <p className="mt-1">Maximum 60 Characters</p>
                            {errors.description && (
                              <div className="text-danger mt-1">{errors.description}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-lg-12">
            <div className="btn-addprize mb-4">
              <button
                type="button"
                className="btn btn-cancel me-2"
                onClick={() => {
                  navigate(route.tierList);
                }}
              >
                Cancel
              </button>
              <button type="button" className="btn btn-submit" onClick={handleSaveTier}>
                Save Tier
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTier;
